import ImageInput from '@/common/components/ImageInput.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import {
  ConvertStringToDate,
  DatePicker,
  formatDateToYMD,
  GetCurrentFormattedDate,
} from '@/common/components/datepicker.tsx'
import { americanBanks, LOG_TYPES, priorityLevels } from '@/common/constants.ts'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import { CommonQueries } from '@/queries/common/queries.ts'
import { ProgramQueries } from '@/queries/program/queries.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import {
  ChangeEvent,
  FormEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useSelector } from 'react-redux'
// import { CardContent } from '@/common/components/Card'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import MultipleSelector, {
  ListItem,
} from '@/common/components/MultipleSelector.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { UpdateProgram } from '@/queries/program/requests.ts'
import {
  SetupProgramProps,
  SetupProgramSchema,
} from '@/queries/program/schemas'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { RootState } from '@/storage/store.ts'
import { useNavigate } from 'react-router-dom'
import { useHasPrivilege } from '../../../../hook/useHasPrivilege.tsx'
import usePermissionAlert from '../../../../hook/usePermissionAlert.tsx'
import { nonUSStates, StatusOptions } from './const.ts'

import { LogUserActivity } from '@/apis/users/requests.ts'
import { Button } from '@/app/common/components/button/Button.tsx'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { Switch } from '@/common/components/Switch.tsx'
import { GetGlobalAchievementTrackingOptions } from '@/queries/achievement_tracking_options/queries.ts'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import Select, { GroupBase, Props } from 'react-select'
import ProgramCatalogCard from './catalogs/CatalogView.tsx'

const aceptableDocuments = [
  { id: 'form_1040', name: 'Form 1040' },
  { id: 'form_w2', name: 'Form W2' },
  { id: 'form_1099', name: 'Form 1099' },
]

const documentsFields = [
  { id: 'form_1040_taxyear', name: 'Form 1040 - Tax Year' },
  {
    id: 'form_1040_household_income_box11',
    name: 'Form 1040 - Household Income Box11',
  },
  {
    id: 'form_1040_household_income_box9',
    name: 'Form 1040 - Household Income+ Box9',
  },
  {
    id: 'form_1040_22_taxpayer_fullname',
    name: 'Form 1040 2022 - Tax Payer Full Name',
  },
  {
    id: 'form_1040_22_taxpayer_firstname',
    name: 'Form 1040 2022 - Tax Payer First Name',
  },
  {
    id: 'form_1040_22_taxpayer_lastname',
    name: 'Form 1040 2022 - Tax Payer Last Name',
  },
  {
    id: 'form_1040_22_household_income_box11',
    name: 'Form 1040 2022 - Household Income Box11',
  },
  {
    id: 'form_1040_22_household_income_box8d',
    name: 'Form 1040 2022 - Household Income Box8d',
  },
  { id: 'form_w2_taxyear', name: 'Form W2 - Tax Year' },
  { id: 'form_w2_fullname', name: 'Form W2 - Employee Full Name' },
  {
    id: 'form_w2_wagestipscomp',
    name: 'Form W2 - Wages Tips and Other Compesations',
  },
  { id: 'form_1099_taxyear', name: 'Form 1099 - Tax Year' },
  { id: 'form_1099_fullname', name: 'Form 1099 - Recipient Full Name' },
  { id: 'form_1099_box1', name: 'Form 1099 - Household Income Box1' },
]

const MIN_AGE = 0 // Minimum eligible age
const MAX_AGE = 25 // Maximum eligible age

const generateAgeOptions = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => min + i)

const CardForm = ({
  children,
  onSubmitHandler,
}: ChildrenProp & { onSubmitHandler: FormEventHandler<HTMLFormElement> }) => {
  return (
    <form
      className="bg-white shadow-md ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2"
      onSubmit={onSubmitHandler}
    >
      {children}
    </form>
  )
}

type MultiSelectOption = {
  value: string | number
  label: string
}
function MultiSelect<
  Option,
  IsMulti extends boolean = true,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>) {
  return (
    <Select
      {...props}
      className="remove-input-txt-border"
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: '#f7941e',
          primary75: '#ffac33',
          primary50: '#ffd6a3',
          primary25: '#ffe8d1',
        },
      })}
    />
  )
}

export default function ProgramView() {
  const navigate = useNavigate()

  const { fireAlert } = useAppAlert()

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [selectedDocuments, setSelectedDocuments] = useState<ListItem[]>([])
  const [selectedDocumentsFields, setSelectedDocumentsFields] = useState<
    ListItem[]
  >([])

  const programDetailsQuery = useQuery(
    ProgramQueries.getProgramById(selectedProgramYear?.id)
  )

  const { data: ProgramData } = programDetailsQuery
  const { data: stateList } = useQuery(CommonQueries.FetchStateByContry(3))
  const ProgramUsers = useQuery(
    ProgramQueries.FetchProgramUsers(selectedProgramYear?.id)
  )
  const { data: AchievementOptionsData } = useQuery(
    GetGlobalAchievementTrackingOptions()
  )
  const achievementTrackingOptions = AchievementOptionsData
    ? AchievementOptionsData.map((option) => ({
        value: String(option.id),
        label: option.name,
      }))
    : []

  const { showPermissionDeniedMessage } = usePermissionAlert()

  const [inviteIsFetching, setInviteIsFetching] = useState(false)

  const canEditPrograms = useHasPrivilege('programs_edit')
  const [shouldDeletePhoto, setShouldDeletePhoto] = useState(false)
  const ageOptions = generateAgeOptions(MIN_AGE, MAX_AGE)

  const {
    register,
    handleSubmit,

    control,
    setValue,
    getValues,
    formState: { errors },
    reset,
    watch,
  } = useForm<SetupProgramProps>({
    resolver: zodResolver(SetupProgramSchema),
    defaultValues: {
      program_start_date: selectedProgramYear?.program_start_date
        ? new Date(selectedProgramYear.program_start_date)
        : new Date('2025-09-01'),
      program_end_date: selectedProgramYear?.program_end_date
        ? new Date(selectedProgramYear.program_end_date)
        : new Date('2026-05-10'),
      enrollment_start_date: selectedProgramYear?.enrollment_start_date
        ? new Date(selectedProgramYear.enrollment_start_date)
        : new Date('2025-01-06'),
      enrollment_end_date: selectedProgramYear?.enrollment_end_date
        ? new Date(selectedProgramYear.enrollment_end_date)
        : new Date('2025-03-30'),
    },
  })

  const programName = watch('name')
  const programStartDate = watch('program_start_date')
  const enrollmentStartDate = watch('enrollment_start_date')
  const applicationStartDate = watch('new_application_start_date')
  const allowManualAchievementTracking = watch(
    'allow_manual_achievement_verification'
  )
  const reimbursementModule = watch('reimbursement_module')
  const { fields: povertyLevels, append } = useFieldArray({
    name: 'federal_poverty_levels',
    control,
  })

  const onSubmit: SubmitHandler<SetupProgramProps> = async (formData) => {
    setInviteIsFetching(true)
    const { error } = await UpdateProgram(
      {
        name: formData?.name ?? '',
        enrollment_start_date:
          formData.enrollment_start_date instanceof Date
            ? formatDateToYMD(formData.enrollment_start_date)
            : formatDateToYMD(new Date('2025-01-06')),
        enrollment_end_date:
          formData.enrollment_end_date instanceof Date
            ? formatDateToYMD(formData.enrollment_end_date)
            : formatDateToYMD(new Date('2025-03-30')),
        new_application_start_date:
          formData.new_application_start_date instanceof Date
            ? formatDateToYMD(formData.new_application_start_date)
            : formatDateToYMD(new Date('2025-01-01')),
        new_application_end_date:
          formData.new_application_end_date instanceof Date
            ? formatDateToYMD(formData.new_application_end_date)
            : formatDateToYMD(new Date('2025-02-01')),
        program_start_date:
          formData.program_start_date instanceof Date
            ? formatDateToYMD(formData.program_start_date)
            : formatDateToYMD(new Date('2025-09-01')),
        program_end_date:
          formData.program_end_date instanceof Date
            ? formatDateToYMD(formData.program_end_date)
            : formatDateToYMD(new Date('2026-05-10')),
        acceptable_income_year: formData.acceptable_income_year || '',
        global_state_id: Number(formData.global_state_id) || 0,
        manager_id:
          formData.manager_id &&
          ProgramUsers.data?.some((e) => e.id === formData.manager_id)
            ? formData.manager_id
            : 0,
        program_image: shouldDeletePhoto
          ? ''
          : formData.program_picture_image || '',
        program_image_path: shouldDeletePhoto
          ? ''
          : formData.program_image || '',
        class_wallet: Number(formData.class_wallet) || 0,
        verify_income: Number(formData.verify_income) || 0,
        unrulr: Number(formData.unrulr) || 0,
        priority_level: formData.priority_level ?? 0,
        income_verification: formData.income_verification || '',
        acceptable_documents: selectedDocuments.length
          ? selectedDocuments.map((filter) => filter.id).join(',')
          : '',
        documents_fields: selectedDocumentsFields.length
          ? selectedDocumentsFields.map((filter) => filter.id).join(',')
          : '',
        federal_poverty_levels: formData.federal_poverty_levels ?? [],
        program_appropiation_amount:
          formData.program_appropiation_amount || '0',
        reward_amount: formData.reward_amount || '0',
        awarded_scholarships_number:
          formData.awarded_scholarships_number || '0',
        minimum_age: formData.minimum_age ?? 0,
        maximum_age: formData.maximum_age ?? 0,
        cut_off_date: formData.cut_off_date
          ? formatDateToYMD(formData.cut_off_date)
          : '',
        military_exception: Number(formData.military_exception) || 0,
        allow_manual_achievement_verification:
          Number(formData.allow_manual_achievement_verification) || 0,
        achievement_option_ids: (formData.achievement_options ?? []).map(
          (option) => Number(option.value)
        ),
        residency_state: Number(formData.residency_state) || 0,
        appeals_window: Number(formData.appeals_window) || 0,
        send_bill_module: formData.send_bill_module
          ? Number(formData.send_bill_module)
          : null,
        marketplace_module: formData.marketplace_module
          ? Number(formData.marketplace_module)
          : null,
        reimbursement_module: formData.reimbursement_module
          ? Number(formData.reimbursement_module)
          : null,
        require_family_bank_info_for_reimbursement:
          formData.require_family_bank_info_for_reimbursement
            ? Number(formData.require_family_bank_info_for_reimbursement)
            : null,
        pay_provider_module: formData.pay_provider_module
          ? Number(formData.pay_provider_module)
          : null,
        require_provider_bank_info_for_transactions:
          formData.require_provider_bank_info_for_transactions
            ? Number(formData.require_provider_bank_info_for_transactions)
            : null,
        item_sale_tax_enabled: formData.item_sale_tax_enabled
          ? Number(formData.item_sale_tax_enabled)
          : null,
        service_fee_charged_to: formData.service_fee_charged_to
          ? Number(formData.service_fee_charged_to)
          : null,
        service_fee: formData.service_fee,
      },
      selectedProgramYear!.id
    )
    setInviteIsFetching(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      return
    } else {
      fireAlert({ variant: 'success', message: 'Program updated successfully' })
      reset()
      programDetailsQuery.refetch()
    }
  }

  const handleImageChange = (file: File | null) => {
    setValue('program_picture_image', file ?? undefined)
    if (!file) setValue('program_image', null)
    setShouldDeletePhoto(file === null ? true : false)
  }

  const filterBySelectedDocuments = (field: ListItem) => {
    let found = false

    selectedDocuments.forEach((doc) => {
      if (field.id.toString().includes(String(doc.id))) found = true
    })

    return found
  }

  useEffect(() => {
    if (ProgramData) {
      setSelectedDocuments(
        aceptableDocuments.filter((doc) =>
          ProgramData?.acceptable_documents?.split(',').includes(doc.id)
        )
      )
      setSelectedDocumentsFields(
        documentsFields.filter((doc) =>
          ProgramData?.documents_fields?.split(',').includes(doc.id)
        )
      )
      const transformedProgramData = {
        ...ProgramData,
        achievement_options: ProgramData.achievement_option_ids.map(
          (optionId) => ({
            value: String(optionId),
            label: achievementTrackingOptions.find(
              (achievementOption) => Number(achievementOption.value) == optionId
            )?.label,
          })
        ),
      }
      reset(transformedProgramData)
      if (!ProgramData.federal_poverty_levels?.length) {
        append(
          Array(8)
            .fill(true)
            .map((_r, i) => ({
              id: null,
              persons_household: Number(i + 1),
              poverty_guideline: '',
            }))
        )
      }
    }
  }, [ProgramData, AchievementOptionsData, append, reset])

  const handleProgramAppropriationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value.replace(/\D/g, '')
    setValue('program_appropiation_amount', newValue)
    setAwardedScholarships()
  }

  const handleRewardAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value.replace(/\D/g, '')
    setValue('reward_amount', newValue)
    setAwardedScholarships()
  }

  const setAwardedScholarships = () => {
    if (
      (getValues('program_appropiation_amount') ?? '').length > 0 &&
      (getValues('reward_amount') ?? '').length > 0
    ) {
      setValue(
        'awarded_scholarships_number',
        Math.ceil(
          parseInt(getValues('program_appropiation_amount') ?? '0') /
            parseInt(getValues('reward_amount') ?? '0')
        ).toString()
      )
    }
  }

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Programs',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  // Function to format input to have max 2 decimal places
  const formatToTwoDecimals = (value: number): number => {
    return Number(value.toFixed(2))
  }

  const handlePercentageInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const rawValue = e.target.value
      .replace(/%/g, '')
      .replace(/^(\d*\.\d*).*$/, '$1')

    const parsed = parseFloat(rawValue)
    if (rawValue === '' || (!isNaN(parsed) && parsed <= 100)) {
      setTimeout(() => {
        const pos = rawValue.length
        e.target.setSelectionRange(pos, pos)
      }, 0)
      onChange(rawValue)
    }
  }

  const handlePercentageBlur = (
    value: string | number | null | undefined,
    onChange: (val: string) => void
  ) => {
    const numericValue = parseFloat(value?.toString() || '')
    if (!isNaN(numericValue)) {
      const formattedValue = formatToTwoDecimals(numericValue)
      onChange(formattedValue.toString())
    } else {
      onChange('0')
    }
  }

  return (
    <div className="space-y-10 divide-y divide-gray-900/10 my-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-8 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Program Information
          </h2>
          {/* <p className="mt-1 text-sm leading-6 text-gray-600">Use a permanent address where you can receive mail.</p> */}
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-1">
                <div className="w-20 h-20">
                  <Controller
                    name="program_image"
                    control={control}
                    render={({ field }) => (
                      <ImageInput
                        onImageChange={handleImageChange}
                        imageUrl={field.value ?? ''}
                        placeholderUrl="/media/avatars/placeholder.jpeg"
                      />
                    )}
                  />
                  <FormErrorMessage message={errors.program_image?.message} />
                </div>
              </div>
              <div className="sm:col-span-5">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Program Name
                </label>
                <div className="mt-2">
                  <Input
                    {...register('name')}
                    readOnly
                    type="text"
                    name="name"
                    autoComplete="given-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                  <FormErrorMessage message={errors.name?.message} />
                </div>
              </div>

              <div
                id="program_start_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="program_start_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Program School Year Start Date
                </label>
                <Controller
                  control={control}
                  name="program_start_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      allowFutureDates={true}
                    />
                  )}
                />
              </div>

              <div
                id="program_end_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="program_end_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Program School Year End Date
                </label>
                <Controller
                  control={control}
                  name="program_end_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      minDate={ConvertStringToDate(programStartDate)}
                      allowFutureDates={true}
                    />
                  )}
                />
                <FormErrorMessage message={errors.program_end_date?.message} />
              </div>

              <div
                id="enrollment_start_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="enrollment_start_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Date Renewal Application Window Opens
                </label>
                <Controller
                  control={control}
                  name="enrollment_start_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      allowFutureDates={true}
                    />
                  )}
                />
              </div>

              <div
                id="enrollment_end_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="enrollment_end_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Date Renewal Application Window Closes
                </label>
                <Controller
                  control={control}
                  name="enrollment_end_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      minDate={ConvertStringToDate(enrollmentStartDate)}
                      allowFutureDates={true}
                    />
                  )}
                />
                <FormErrorMessage
                  message={errors.enrollment_end_date?.message}
                />
              </div>

              <div
                id="new_application_start_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="new_application_start_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Date New Application Window Opens
                </label>
                <Controller
                  control={control}
                  name="new_application_start_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      allowFutureDates={true}
                    />
                  )}
                />
              </div>

              <div
                id="new_application_end_date"
                className="col-span-full 2xl:col-span-3"
              >
                <label
                  htmlFor="new_application_end_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Date New Application Window Closes
                </label>
                <Controller
                  control={control}
                  name="new_application_end_date"
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      minDate={ConvertStringToDate(applicationStartDate)}
                      allowFutureDates={true}
                    />
                  )}
                />
                <FormErrorMessage
                  message={errors.new_application_end_date?.message}
                />
              </div>

              <div className="col-span-full 2xl:col-span-3">
                <label
                  htmlFor="acceptable_income_year"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Pick Acceptable Income Year
                </label>
                <Input
                  id="acceptable_income_year"
                  {...register('acceptable_income_year')}
                  type="text"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:ring-1 focus:ring-inset focus:ring-gray-300"
                />
                <FormErrorMessage
                  message={errors.acceptable_income_year?.message}
                />
              </div>
              <div className="col-span-full 2xl:col-span-3"></div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="global_state_id"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  State
                </label>
                <div className="mt-2">
                  <Controller
                    name="global_state_id"
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value ?? 0}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          const value = Number(e.target.value)
                          field.onChange(value)
                        }}
                        autoComplete="global_state_id"
                        className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:leading-6 w-full"
                      >
                        {stateList?.data
                          .filter((f) => !nonUSStates.includes(f.id))
                          .map((state) => (
                            <option key={state.id} value={Number(state.id)}>
                              {state.name}
                            </option>
                          ))}
                      </select>
                    )}
                  />
                  <FormErrorMessage message={errors.global_state_id?.message} />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="manager_id"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Program Manager
                </label>
                <div className="mt-2">
                  <Controller
                    control={control}
                    name="manager_id"
                    render={({ field }) => (
                      <select
                        value={field.value ?? 0}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          if (e.target.value == 'Add_new')
                            return navigate(AppRoutePaths.setup.users.index)
                          field.onChange(Number(e.target.value))
                        }}
                        autoComplete="Program Manager"
                        className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:leading-6 w-full"
                      >
                        <option key={0} value={''}>
                          Select Program Manager
                        </option>
                        {ProgramUsers.data
                          ?.filter((u) => u.roles?.includes('program_manager'))
                          .map((state) => (
                            <option key={state.id} value={Number(state.id)}>
                              {state.first_name} {state.last_name}
                            </option>
                          ))}
                        <option key={'Add_new'} value={'Add_new'}>
                          Add New
                        </option>
                      </select>
                    )}
                  />
                  <FormErrorMessage message={errors.manager_id?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="created_by"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Program Created By
                </label>
                <div className="mt-2">
                  <Input
                    readOnly={true}
                    {...register('created_by')}
                    id="created_by"
                    type="text"
                    placeholder="Name of Program Creator"
                    autoComplete="given-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:ring-1 focus:ring-inset focus:ring-gray-300"
                  />
                </div>
              </div>
              {/* //TODO: CHANGE OF THIS FROM LINE TO LAST LINE
                        INPUT: REGISTER
                        AND CONTROLLERS WITH SELECT
                    */}
              <div className="sm:col-span-3">
                <label
                  htmlFor="program-creation-date"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Program Creation Date
                </label>
                <div className="mt-2">
                  <Input
                    id="program-creation-date"
                    name="program-creation-date"
                    type="text"
                    value={GetCurrentFormattedDate(
                      ProgramData?.created_datetime ?? new Date()
                    )}
                    readOnly={true}
                    autoComplete="given-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:ring-1 focus:ring-inset focus:ring-gray-300"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            {inviteIsFetching ? (
              <EllipsesLoader size="md" />
            ) : (
              <>
                <Button
                  size="lg"
                  type="submit"
                  className="px-4 py-2 rounded text-white font-semibold"
                  onClick={() => {
                    if (!canEditPrograms) {
                      showPermissionDeniedMessage()
                    }
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </CardForm>
        <div></div>
        <div className="col-span-2">
          <ProgramCatalogCard />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Financial Information
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="max-w-2xl space-y-10">
              {/* Selector for Banks */}
              <div className="sm:col-span-4">
                <label
                  htmlFor="bank"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Bank
                </label>
                <div className="mt-2">
                  <select
                    id="bank"
                    name="bank"
                    autoComplete="bank-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:max-w-xs sm:text-sm sm:leading-6"
                  >
                    {americanBanks.map((bank) => (
                      <option key={bank.abbreviation} value={bank.abbreviation}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bank Account */}
              <div className="sm:col-span-3">
                <label
                  htmlFor="bank-account"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Bank Account
                </label>
                <div className="mt-2">
                  <Input
                    id="bank-account"
                    name="bank-account"
                    type="text"
                    placeholder={'0000000000000'}
                    autoComplete="given-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage()
                }
              }}
            >
              Save
            </button>
          </div>
        </CardForm>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Scholarship Rules
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="max-w-2xl space-y-10">
              <div className="">
                <label
                  htmlFor="program_appropiation_amount"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Program Appropriation Amount
                </label>
                <div className="relative mt-2 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <Input
                    {...register('program_appropiation_amount')}
                    name="program_appropiation_amount"
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    onChange={handleProgramAppropriationChange}
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span
                      id="amount-currency"
                      className="text-gray-500 sm:text-sm"
                    >
                      USD
                    </span>
                  </div>
                  <FormErrorMessage
                    message={errors.program_appropiation_amount?.message}
                  />
                </div>
              </div>

              {/* Scholarship Amount in USD with currency auto-completion */}
              <div>
                <label
                  htmlFor="reward_amount"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Reward Amount
                </label>
                <div className="relative mt-2 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <Input
                    {...register('reward_amount')}
                    name="reward_amount"
                    type="text"
                    placeholder="0.00"
                    aria-describedby="amount-currency"
                    className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    onChange={handleRewardAmountChange}
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span
                      id="amount-currency"
                      className="text-gray-500 sm:text-sm"
                    >
                      USD
                    </span>
                  </div>
                </div>
              </div>

              <div className="">
                <label
                  htmlFor="awarded_scholarships_number"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Number of Awarded Scholarships
                </label>
                <div className="mt-2">
                  <Input
                    {...register('awarded_scholarships_number')}
                    name="awarded_scholarships_number"
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                  <FormErrorMessage
                    message={errors.awarded_scholarships_number?.message}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage()
                }
              }}
            >
              Save
            </button>
          </div>
        </CardForm>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Eligibility
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="minimum_age"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Minimum Age
                </label>
                <div className="mt-2">
                  <Controller
                    control={control}
                    name="minimum_age"
                    render={({ field }) => (
                      <select
                        value={field.value ?? 0}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          field.onChange(Number(e.target.value))
                        }}
                        className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:leading-6 w-full"
                      >
                        <option key={0} value={''}>
                          Select Minimum Age
                        </option>
                        {ageOptions.map((age) => (
                          <option key={age} value={age}>
                            {age}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  <FormErrorMessage message={errors.manager_id?.message} />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="minimum_age"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Maximum Age
                </label>
                <div className="mt-2">
                  <Controller
                    control={control}
                    name="maximum_age"
                    render={({ field }) => (
                      <select
                        value={field.value ?? 0}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          field.onChange(Number(e.target.value))
                        }}
                        className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:leading-6 w-full"
                      >
                        <option key={0} value={''}>
                          Select Maximum Age
                        </option>
                        {ageOptions.map((age) => (
                          <option key={age} value={age}>
                            {age}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  <FormErrorMessage message={errors.manager_id?.message} />
                </div>
              </div>
              <div className="col-span-full 2xl:col-span-3">
                <label
                  htmlFor="cut_off_date"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Age Date Cutoff
                </label>
                <Controller
                  control={control}
                  name="cut_off_date"
                  render={({ field }) => (
                    <DatePicker
                      readonly
                      name={field.name}
                      initialDate={ConvertStringToDate(field.value)}
                      onDateChange={field.onChange}
                      allowFutureDates={true}
                    />
                  )}
                />
              </div>
              <div className="col-span-full 2xl:col-span-6">
                <label
                  htmlFor="residency_state"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Establish Residency State
                </label>
                <Controller
                  name="residency_state"
                  control={control}
                  render={({ field }) => (
                    <select
                      disabled
                      value={field.value ?? 0}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        const value = Number(e.target.value)
                        field.onChange(value)
                      }}
                      className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:leading-6 w-full"
                    >
                      <option key={0} value={''}>
                        Select State
                      </option>
                      {stateList?.data
                        .filter((f) => !nonUSStates.includes(f.id))
                        .map((state) => (
                          <option key={state.id} value={Number(state.id)}>
                            {state.name}
                          </option>
                        ))}
                    </select>
                  )}
                />
                <FormErrorMessage message={errors.residency_state?.message} />
              </div>
              <div className="col-span-full 2xl:col-span-6">
                <label
                  htmlFor="military_exception"
                  className="block text-sm font-medium leading-6 text-gray-900 my-2"
                >
                  Military exception is available
                </label>
                <Controller
                  name="military_exception"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      disabled
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
                <FormErrorMessage message={errors.residency_state?.message} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage()
                }
              }}
            >
              Save
            </button>
          </div>
        </CardForm>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Rules
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="max-w-2xl space-y-10">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Class Wallet
                </label>
                <Controller
                  control={control}
                  name="class_wallet"
                  render={({ field }) => (
                    <select
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={''}>Select</option>
                      {StatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <FormErrorMessage message={errors.class_wallet?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unrulr</label>
                <Controller
                  control={control}
                  name="unrulr"
                  render={({ field }) => (
                    <select
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={''}>Select</option>
                      {StatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <FormErrorMessage message={errors.unrulr?.message} />
              </div>
              <div>
                <label
                  htmlFor="appeals_window"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Appeals Window
                </label>
                <div className="mt-2">
                  <Input
                    {...register('appeals_window')}
                    name="appeals_window"
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                  <FormErrorMessage message={errors.appeals_window?.message} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Allow Manual Achievement Tracking
                </label>
                <select
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  {...register('allow_manual_achievement_verification')}
                >
                  {StatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FormErrorMessage
                  message={
                    errors.allow_manual_achievement_verification?.message
                  }
                />
              </div>
              {allowManualAchievementTracking == 1 && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Achievement Tracking Options
                  </label>
                  <Controller
                    name="achievement_options"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect<MultiSelectOption>
                        options={achievementTrackingOptions}
                        isMulti={true}
                        onChange={(selectedOptions) => {
                          field.onChange(selectedOptions)
                        }}
                        value={field.value}
                      />
                    )}
                  />
                  <FormErrorMessage
                    message={errors.achievement_options?.message}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage()
                }
              }}
            >
              Save
            </button>
          </div>
        </CardForm>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Priority / Preference
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="max-w-2xl space-y-10">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="priority_level"
                >
                  Priority Level
                </label>
                <Controller
                  control={control}
                  name="priority_level"
                  render={({ field }) => (
                    <select
                      disabled
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={''}>Select</option>
                      {priorityLevels.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <FormErrorMessage message={errors.priority_level?.message} />
              </div>

              <div>
                <label
                  htmlFor="income_verification"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Income Verification Year
                </label>
                <div className="mt-2">
                  <Input
                    readOnly
                    {...register('income_verification')}
                    type="text"
                  />
                  <FormErrorMessage message={errors.name?.message} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Acceptable Documents
                </label>
                <MultipleSelector
                  readOnly
                  list={aceptableDocuments}
                  selected={selectedDocuments}
                  setSelected={setSelectedDocuments}
                  placeHolder="Select Documents"
                  icon
                />
                <FormErrorMessage
                  message={errors.income_verification?.message}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Documents Fields (OCR)
                </label>
                <MultipleSelector
                  readOnly
                  list={documentsFields.filter(filterBySelectedDocuments)}
                  selected={selectedDocumentsFields}
                  setSelected={setSelectedDocumentsFields}
                  placeHolder="Select Documents Fields"
                  icon
                />
                <FormErrorMessage
                  message={errors.income_verification?.message}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="federal_poverty_levels"
                >
                  Federal Poverty Level
                </label>
                <div className="grid grid-cols-2">
                  <div className="text-center">Persons in family/household</div>
                  <div className="text-center">Poverty Guideline</div>
                </div>
                {povertyLevels.map((field, index) => {
                  return (
                    <div className="grid grid-cols-2" key={field.id ?? index}>
                      <div className="relative mt-2 rounded-md shadow-sm">
                        <Input
                          type="number"
                          readOnly
                          className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                          {...register(
                            `federal_poverty_levels.${index}.persons_household`
                          )}
                        />
                      </div>

                      <div className="relative mt-2 rounded-md shadow-sm">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <Input
                          {...register(
                            `federal_poverty_levels.${index}.poverty_guideline`
                          )}
                          readOnly
                          className="block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        />
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <span
                            id="amount-currency"
                            className="text-gray-500 sm:text-sm"
                          >
                            USD
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* <Controller
                  control={control}
                  name='federal_poverty_levels'
                  render={({ field }) =>
                    <select
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={''}>Select</option>
                      {ProgramData?.federal_poverty_levels.map((option) => (
                        <option key={option.id} value={option.id}>
                          {`${option.persons_household} person${option.persons_household != 1 ? 's' : ''} - ${option.poverty_guideline}`}
                        </option>
                      ))}
                    </select>
                  }
                /> */}
                <FormErrorMessage
                  message={errors.federal_poverty_levels?.message}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Verify Income for Renewals
                </label>
                <Controller
                  control={control}
                  name="verify_income"
                  render={({ field }) => (
                    <select
                      disabled
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={''}>Select</option>
                      {StatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <FormErrorMessage message={errors.verify_income?.message} />
              </div>
            </div>
          </div>
          {/*  <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage();
                }
              }}
            >
              Save
            </button>
          </div> */}
        </CardForm>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 2xl:grid-cols-3">
        <div className="px-4 sm:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Spend Rules
          </h2>
        </div>

        <CardForm onSubmitHandler={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:p-8">
            <div className="max-w-2xl space-y-8">
              <div>
                <label
                  htmlFor="marketplace_module"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Marketplace Module Is Enabled
                </label>
                <Controller
                  name="marketplace_module"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="send_bill_module"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Send Bill Module Is Enabled
                </label>
                <Controller
                  name="send_bill_module"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="reimbursement_module"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Reimbursement Module Is Enabled
                </label>
                <Controller
                  name="reimbursement_module"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
              {reimbursementModule == 1 && (
                <div className="ml-16">
                  <label
                    htmlFor="require_family_bank_info_for_reimbursement"
                    className="block text-sm font-medium text-gray-900 my-2"
                  >
                    Require Family Bank Info for Reimbursements
                  </label>
                  <Controller
                    name="require_family_bank_info_for_reimbursement"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field?.value == 1 ? true : false}
                        onCheckedChange={(checked) => {
                          field.onChange(checked ? 1 : 0)
                        }}
                      ></Switch>
                    )}
                  />
                </div>
              )}
              <div>
                <label
                  htmlFor="pay_provider_module"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Pay Provider Module Is Enabled
                </label>
                <Controller
                  name="pay_provider_module"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="service_fee"
                  className="block text-sm font-medium text-gray-900 mt-2"
                >
                  Payment Service Fee Charge
                </label>

                <Controller
                  name="service_fee"
                  control={control}
                  render={({ field }) => (
                    <div className="relative mt-2">
                      <Input
                        id="service_fee"
                        type="text"
                        placeholder="0.0%"
                        value={field.value ? `${field.value}%` : ''}
                        onChange={(e) =>
                          handlePercentageInputChange(e, field.onChange)
                        }
                        onBlur={() =>
                          handlePercentageBlur(field.value, field.onChange)
                        }
                        className="pr-8"
                      />
                    </div>
                  )}
                />
              </div>
              <div className="ml-16">
                <label
                  htmlFor="service_fee_charged_to"
                  className="block text-sm font-medium text-gray-900 mb-4"
                >
                  Who will pay for the service fee?
                </label>
                <Controller
                  name="service_fee_charged_to"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      onValueChange={(value) => {
                        setValue('service_fee_charged_to', parseInt(value))
                      }}
                      value={(field.value ?? '').toString()}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="provider" />
                        <label
                          htmlFor="provider"
                          className="block text-sm text-gray-900"
                        >
                          Provider
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2" id="applicant" />
                        <label
                          htmlFor="applicant"
                          className="block text-sm text-gray-900"
                        >
                          Applicant
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="3" id="program" />
                        <label
                          htmlFor="program"
                          className="block text-sm text-gray-900"
                        >
                          {programName}
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="0" id="no_fee" />
                        <label
                          htmlFor="no_fee"
                          className="block text-sm text-gray-900"
                        >
                          No Fee
                        </label>
                      </div>
                    </RadioGroup>
                  )}
                />
                <FormErrorMessage
                  message={errors.service_fee_charged_to?.message}
                />
              </div>
              <div>
                <label
                  htmlFor="require_provider_bank_info_for_transactions"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Require Provider Bank Info for Transactions
                </label>
                <Controller
                  name="require_provider_bank_info_for_transactions"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="item_sale_tax_enabled"
                  className="block text-sm font-medium text-gray-900 my-2"
                >
                  Item Sales Tax Is Enabled
                </label>
                <Controller
                  name="item_sale_tax_enabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field?.value == 1 ? true : false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 1 : 0)
                      }}
                    ></Switch>
                  )}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              onClick={() => {
                if (!canEditPrograms) {
                  showPermissionDeniedMessage()
                }
              }}
            >
              Save
            </button>
          </div>
        </CardForm>
      </div>
    </div>
  )
}
